import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';

import type { Invitee } from 'bundles/enterprise-admin-learners/components/invite-learners/types';
import AddUsersToProgramMutationGql from 'bundles/enterprise-admin-program-creation/queries/AddUsersToProgram.graphql';
import type {
  AddUsersToProgramMutation,
  AddUsersToProgramMutationVariables,
} from 'bundles/enterprise-admin-program-creation/queries/__generated__/AddUsersToProgram';

import _t from 'i18n!nls/enterprise-admin-learners';

export type FailedInvite = { email: string; errorCode: string; message: string };
export type SuccessfulInvite = { programInvitationId: string; email: string; firstInvitation: boolean };

export type InviteLearnersResult = {
  successful: SuccessfulInvite[];
  failed: FailedInvite[];
  /** Emails added as direct program memberships instead of invitations (e.g. existing users). */
  membershipEmails: string[];
  programId: string;
};

type InviteLearnersInput = { programId: string; invitees: Invitee[] } | { orgId: string; invitees: Invitee[] };

/**
 * Wraps `ProgramInvitations_AddUsersToProgram` for the org-level invite flow. Unlike
 * `useAddLearnersToGroup`, returns the full `failed` array (not just a count) for ENTADMIN-1957's
 * error report.
 *
 * Accepts either `programId` (group-scoped) or `orgId` (org admin skip-group path,
 * ENTADMIN-1961) — never both; the backend resolves `orgId` to the org's hidden System
 * Program server-side.
 */
export const useInviteLearners = () => {
  const [addUsersToProgram, { loading: submitting }] = useMutation<
    AddUsersToProgramMutation,
    AddUsersToProgramMutationVariables
  >(AddUsersToProgramMutationGql, {
    context: { clientName: 'gatewayGql' },
  });

  const inviteLearners = async (input: InviteLearnersInput): Promise<InviteLearnersResult> => {
    try {
      const response = await addUsersToProgram({
        variables: { input },
      });
      const result = response.data?.ProgramInvitations_AddUsersToProgram;
      if (result?.__typename === 'ProgramInvitations_AddUsersToProgramSuccess') {
        return {
          successful: result.successful,
          failed: result.failed,
          membershipEmails: result.membershipEmails,
          programId: result.programId,
        };
      }
      throw new Error(
        result?.__typename === 'ProgramInvitations_AddUsersToProgramFailure' ? result.message : JSON.stringify(response)
      );
    } catch (err) {
      Sentry.captureException(err);
      throw err instanceof Error ? err : new Error(_t("We couldn't send invitations. Please try again."));
    }
  };

  return { inviteLearners, submitting };
};
